// frontend/src/pages/dashboard/PartnerDashboard.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotificationApi } from '../../context/NotificationContext';
import { FiAlertCircle, FiRefreshCw, FiPieChart, FiTrendingUp, FiPlus, FiUser, FiCalendar, FiArrowRight, FiEye, FiEyeOff } from 'react-icons/fi';
import { Line, Pie } from 'react-chartjs-2';
import { ConfigProvider, theme, Spin, Button, Modal, Table, Tag, Space, Tabs, Badge, Alert, Typography, Tooltip as AntTooltip } from 'antd';
import { 
  Chart as ChartJS, 
  LineElement, 
  PointElement, 
  CategoryScale, 
  LinearScale, 
  ArcElement, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js';
import { 
  ClockCircleOutlined,
  CalendarOutlined, 
  SignatureOutlined, 
  EyeOutlined, 
  WarningOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  FileWordOutlined
} from '@ant-design/icons';
import moment from 'moment';
import api from '../../utils/api';
import './dashboard.css';
import { useContracts } from '../../hooks/useContracts';

const { Text } = Typography;

ChartJS.register(
  LineElement, 
  PointElement, 
  CategoryScale, 
  LinearScale, 
  ArcElement, 
  Tooltip, 
  Legend,
  Filler
);

const PartnerDashboard = () => {
    const { user, isAuthenticated, loading: authLoading, error: authError } = useAuth();
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeView, setActiveView] = useState('all-tickets');
    const [chartView, setChartView] = useState('all-tickets');
    const [pieChartMode, setPieChartMode] = useState('current-month');
    const [isDark, setIsDark] = useState(document.body.classList.contains('dark-mode'));
    const [showOverviewSection, setShowOverviewSection] = useState(true);
    const [showBreakdownSection, setShowBreakdownSection] = useState(true);
    const [showAnalyticsSection, setShowAnalyticsSection] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const notificationApi = useNotificationApi();
    const [showPendingPopup, setShowPendingPopup] = useState(false);
    const [inProgressTickets, setInProgressTickets] = useState([]);
    const [pendingSignatureTickets, setPendingSignatureTickets] = useState([]);
    const popupShownRef = useRef(false);

    // Get the contract IDs this partner is main contractor for
    const mainContractorContractIds = user?.mainContractorContractIds || [];
    const mainContractorName = user?.mainContractorName || 'Main Contractor';

    // Fetch contract names for display
    const { contracts: userContracts, loading: loadingContracts } = useContracts(mainContractorContractIds);

    // Check screen size
    useEffect(() => {
        const checkScreenSize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Unknown';
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (e) {
            return 'Unknown';
        }
    };

    const getAgingDisplay = (days, hours) => {
        if (days === null || days === undefined) return 'N/A';
        if (days === 0) {
            if (hours && hours < 24) {
                const hrs = Math.floor(hours);
                return hrs === 0 ? '< 1 hour' : `${hrs} hour${hrs > 1 ? 's' : ''}`;
            }
            return 'Today';
        }
        if (days === 1) return '1 day';
        return `${days}d`;
    };

    const getAgingColor = (days) => {
        if (days >= 7) return 'red';
        if (days >= 3) return 'orange';
        if (days >= 1) return 'gold';
        return 'green';
    };

    // Fetch all tickets and filter by contract IDs
    const fetchFilteredTickets = useCallback(async () => {
        if (mainContractorContractIds.length === 0) return [];
        
        try {
            const response = await api.get('/api/tickets', { params: { limit: 1000 } });
            const allTickets = response.data.data || [];
            const filteredTickets = allTickets.filter(ticket => 
                mainContractorContractIds.includes(ticket.contractId)
            );
            return filteredTickets;
        } catch (error) {
            console.error('Error fetching filtered tickets:', error);
            return [];
        }
    }, [mainContractorContractIds]);

    const fetchPendingActions = useCallback(async () => {
        try {
            const popupShown = sessionStorage.getItem('pendingActionsPopupShown') === 'true';
            if (popupShown) return;
            
            const response = await api.get('/api/pending-actions');
            if (response.data.success && response.data.data.hasActions) {
                let allPendingTickets = response.data.data.pendingSignatureTickets || [];
                const userId = user?.id;
                const userRoleId = user?.roleId;
                
                const filteredPendingTickets = allPendingTickets.filter(ticket => 
                    mainContractorContractIds.includes(ticket.contractId)
                );
                
                let processedTickets = [];
                if (userRoleId === 1) {
                    processedTickets = filteredPendingTickets.map(ticket => ({
                        ...ticket,
                        requires_my_signature: ticket.signature_type === 'supervisor_signature' && ticket.my_supervisor_id === userId
                    }));
                } else {
                    processedTickets = filteredPendingTickets;
                }
                
                const filteredInProgress = (response.data.data.inProgressTickets || []).filter(ticket => 
                    mainContractorContractIds.includes(ticket.contractId)
                );
                
                setInProgressTickets(filteredInProgress);
                setPendingSignatureTickets(processedTickets);
                setTimeout(() => {
                    setShowPendingPopup(true);
                    sessionStorage.setItem('pendingActionsPopupShown', 'true');
                }, 100);
            } else {
                sessionStorage.setItem('pendingActionsPopupShown', 'true');
            }
        } catch (error) {
            console.error('Error fetching pending actions:', error);
        }
    }, [user, mainContractorContractIds]);

    const handleViewTicketFromPopup = (ticketId) => {
        setShowPendingPopup(false);
        navigate(`/tickets/${ticketId}`);
    };

    const handleSignTicketFromPopup = (ticketId, signatureType) => {
        setShowPendingPopup(false);
        navigate(`/tickets/${ticketId}`, { state: { openSignModal: true, signatureType } });
    };

    useEffect(() => {
        const handleThemeChange = (mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class') {
                    setIsDark(document.body.classList.contains('dark-mode'));
                }
            });
        };
        const observer = new MutationObserver(handleThemeChange);
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!authLoading && !isAuthenticated) navigate('/login');
    }, [isAuthenticated, authLoading, navigate]);

    const showError = (title, content, key = 'dashboardError') => {
        notificationApi.error({ key, message: title, description: content, duration: 4.5, placement: 'topRight' });
    };

    const showSuccess = (title, description, key = 'dashboardSuccess') => {
        notificationApi.success({ key, message: title, description, duration: 3, placement: 'topRight' });
    };

    const showLoading = (key, messageContent) => {
        let dismissNotification;
        const timer = setTimeout(() => {
            dismissNotification = notificationApi.open({ key, message: messageContent, duration: 0, placement: 'topRight' });
        }, 300);
        return () => {
            clearTimeout(timer);
            setTimeout(() => {
                if (dismissNotification) {
                    typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
                } else {
                    notificationApi.destroy(key);
                }
            }, 500);
        };
    };

    const fetchDashboardData = useCallback(async () => {
        if (!isAuthenticated || authLoading || !user) return;
        if (mainContractorContractIds.length === 0) {
            setError('No contracts found for your organization.');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const dismissLoading = showLoading('fetchDashboard', 'Loading partner dashboard...');

        try {
            const filteredTickets = await fetchFilteredTickets();
            
            if (filteredTickets.length === 0) {
                setDashboardData({
                    totalTickets: 0,
                    myTicketsCount: 0,
                    recentTickets: [],
                    recentMyTickets: [],
                    statusCounts: {},
                    myTicketsStatusCounts: {},
                    monthlyVolume: {},
                    monthlyVolumeMy: {},
                    currentMonthStatus: {},
                    currentMonthStatusMy: {}
                });
                dismissLoading();
                setIsLoading(false);
                return;
            }
            
            const totalTickets = filteredTickets.length;
            const myTickets = filteredTickets.filter(t => 
                t.assignedToUserId === user?.id || t.createdByUserId === user?.id
            );
            const myTicketsCount = myTickets.length;
            
            const recentTickets = [...filteredTickets]
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .slice(0, 10);
            
            const recentMyTickets = [...myTickets]
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .slice(0, 10);
            
            const statusCounts = {};
            const myTicketsStatusCounts = {};
            
            filteredTickets.forEach(ticket => {
                const status = ticket.statusName || ticket.status;
                if (status) statusCounts[status] = (statusCounts[status] || 0) + 1;
            });
            
            myTickets.forEach(ticket => {
                const status = ticket.statusName || ticket.status;
                if (status) myTicketsStatusCounts[status] = (myTicketsStatusCounts[status] || 0) + 1;
            });
            
            const monthlyVolume = {};
            const monthlyVolumeMy = {};
            
            filteredTickets.forEach(ticket => {
                if (ticket.caseReportedDate) {
                    const month = moment(ticket.caseReportedDate).format('YYYY-MM');
                    monthlyVolume[month] = (monthlyVolume[month] || 0) + 1;
                }
            });
            
            myTickets.forEach(ticket => {
                if (ticket.caseReportedDate) {
                    const month = moment(ticket.caseReportedDate).format('YYYY-MM');
                    monthlyVolumeMy[month] = (monthlyVolumeMy[month] || 0) + 1;
                }
            });
            
            const currentMonth = moment().format('YYYY-MM');
            const currentMonthStatus = {};
            const currentMonthStatusMy = {};
            
            filteredTickets.forEach(ticket => {
                if (ticket.caseReportedDate && moment(ticket.caseReportedDate).format('YYYY-MM') === currentMonth) {
                    const status = ticket.statusName || ticket.status;
                    if (status) currentMonthStatus[status] = (currentMonthStatus[status] || 0) + 1;
                }
            });
            
            myTickets.forEach(ticket => {
                if (ticket.caseReportedDate && moment(ticket.caseReportedDate).format('YYYY-MM') === currentMonth) {
                    const status = ticket.statusName || ticket.status;
                    if (status) currentMonthStatusMy[status] = (currentMonthStatusMy[status] || 0) + 1;
                }
            });
            
            setDashboardData({
                totalTickets,
                myTicketsCount,
                recentTickets,
                recentMyTickets,
                statusCounts,
                myTicketsStatusCounts,
                monthlyVolume,
                monthlyVolumeMy,
                currentMonthStatus,
                currentMonthStatusMy
            });
            
            showSuccess('Partner Dashboard Loaded', `Showing data for ${mainContractorName}.`);
        } catch (err) {
            console.error('Fetch error:', err);
            const errorMessage = err.response?.data?.message || 'Failed to load partner dashboard data';
            showError('Loading Error', errorMessage);
            setError(errorMessage);
        } finally {
            setIsLoading(false);
            dismissLoading();
        }
    }, [user, isAuthenticated, authLoading, mainContractorContractIds, mainContractorName, fetchFilteredTickets]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    useEffect(() => {
        const popupShown = sessionStorage.getItem('pendingActionsPopupShown') === 'true';
        if (dashboardData && !popupShown) {
            const timer = setTimeout(() => fetchPendingActions(), 300);
            return () => clearTimeout(timer);
        }
    }, [dashboardData, fetchPendingActions]);

    const handleRefresh = () => fetchDashboardData();
    const handleCreateTicket = () => navigate('/tickets/new');

    const handleTicketClick = (ticketUuid) => {
        const allRecentTickets = [
            ...(dashboardData?.recentTickets || []),
            ...(dashboardData?.recentMyTickets || [])
        ];
        const ticket = allRecentTickets.find(t => t.ticketUuid === ticketUuid);
        if (ticket?.id) navigate(`/tickets/${ticket.id}`);
        else showError('Error', 'Could not find ticket details.');
    };

        const handleCardClick = (filterType) => {
        setActiveView(filterType);
        setChartView(filterType);
        let route = '/partner/tickets';  // Changed from '/tickets'
        if (filterType === 'my-tickets') route = '/partner/my-tickets';  // Changed from '/my-tickets'
        navigate(route);
    };

    const handleChartViewChange = (viewType) => setChartView(viewType);
    const handlePieChartModeChange = (mode) => setPieChartMode(mode);
    const toggleOverviewSection = () => setShowOverviewSection(prev => !prev);
    const toggleBreakdownSection = () => setShowBreakdownSection(prev => !prev);
    const toggleAnalyticsSection = () => setShowAnalyticsSection(prev => !prev);

    const getStatusCountsForView = () => {
        if (!dashboardData) return {};
        return {
            'all-tickets': dashboardData.statusCounts || {},
            'my-tickets': dashboardData.myTicketsStatusCounts || {}
        }[activeView] || {};
    };

    const getChartDataForView = () => {
        if (!dashboardData) return { monthlyVolume: {}, currentMonthStatus: {}, allTimeStatus: {} };
        if (chartView === 'my-tickets') {
            return {
                monthlyVolume: dashboardData.monthlyVolumeMy || {},
                currentMonthStatus: dashboardData.currentMonthStatusMy || {},
                allTimeStatus: dashboardData.myTicketsStatusCounts || {}
            };
        }
        return {
            monthlyVolume: dashboardData.monthlyVolume || {},
            currentMonthStatus: dashboardData.currentMonthStatus || {},
            allTimeStatus: dashboardData.statusCounts || {}
        };
    };

    const getRecentTickets = () => {
        if (!dashboardData || !user) return [];
        return (chartView === 'my-tickets' ? dashboardData.recentMyTickets : dashboardData.recentTickets) || [];
    };

    const statusColorMap = {
        'Unassigned': '#040403ff',
        'Waiting For Support': '#f97316',
        'In Progress': '#3b82f6',
        'Waiting For Customer': '#8b5cf6',
        'Escalated': '#ef4444',
        'Resolved': '#10b981',
        'Closed': '#35b910ff',
        'Cancelled': '#d1d5db',
        'Reopened': '#f59e0b',
        'Unknown': '#6b7280'
    };

    const getCssVariable = (variableName) => getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();

    const preparePieChartData = () => {
        const chartData = getChartDataForView();
        const statusData = pieChartMode === 'all-time' ? chartData.allTimeStatus : chartData.currentMonthStatus;
        const labels = Object.keys(statusData);
        const data = labels.map(status => statusData[status] || 0);
        const backgroundColors = labels.map(status => statusColorMap[status] || '#6b7280');
        const totalTickets = data.reduce((sum, val) => sum + val, 0);
        if (totalTickets === 0) {
            return { labels: ['No Tickets'], data: [1], backgroundColors: ['#d1d5db'] };
        }
        return { labels, data, backgroundColors };
    };

    if (authLoading || isLoading) {
        return (
            <ConfigProvider theme={{ algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
                <div className="dashboard-loading">
                    <FiRefreshCw className="animate-spin text-2xl text-primary" />
                    <p>Loading partner dashboard...</p>
                </div>
            </ConfigProvider>
        );
    }

    if (authError || error || !user) {
        const msg = authError || error || 'User information not available';
        return (
            <ConfigProvider theme={{ algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
                <div className="dashboard-error">
                    <FiAlertCircle className="text-danger text-2xl" />
                    <h3>{authError ? 'Authentication Error' : 'Partner Dashboard Error'}</h3>
                    <p>{msg}</p>
                    <button onClick={() => window.location.reload()}>
                        <FiRefreshCw className="mr-2" />
                        Retry
                    </button>
                </div>
            </ConfigProvider>
        );
    }

    const roleName = user?.roleName || 'Partner';
    const userName = user?.firstName || user?.name || 'User';
    const statusCounts = getStatusCountsForView();
    const chartData = getChartDataForView();
    const recentTickets = getRecentTickets();
    const pieChartData = preparePieChartData();
    const currentMonthYear = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

    const cardTiers = [
        [
            { title: 'Total Tickets', value: dashboardData?.totalTickets || 0, type: 'all-tickets', color: '#6b7280', size: 'large', isActive: activeView === 'all-tickets' },
            { title: 'My Tickets', value: dashboardData?.myTicketsCount || 0, type: 'my-tickets', color: '#3b82f6', size: 'large', isActive: activeView === 'my-tickets' }
        ],
        [
            { title: 'Unassigned', value: statusCounts['Unassigned'] || 0, color: statusColorMap['Unassigned'] },
            { title: 'Waiting For Support', value: statusCounts['Waiting For Support'] || 0, color: statusColorMap['Waiting For Support'] },
            { title: 'In Progress', value: statusCounts['In Progress'] || 0, color: statusColorMap['In Progress'] },
            { title: 'Waiting For Customer', value: statusCounts['Waiting For Customer'] || 0, color: statusColorMap['Waiting For Customer'] }
        ],
        [
            { title: 'Escalated', value: statusCounts['Escalated'] || 0, color: statusColorMap['Escalated'] },
            { title: 'Resolved', value: statusCounts['Resolved'] || 0, color: statusColorMap['Resolved'] },
            { title: 'Closed', value: statusCounts['Closed'] || 0, color: statusColorMap['Closed'] },
            { title: 'Cancelled', value: statusCounts['Cancelled'] || 0, color: statusColorMap['Cancelled'] },
            { title: 'Reopened', value: statusCounts['Reopened'] || 0, color: statusColorMap['Reopened'] }
        ]
    ];

    const lineChartData = {
        labels: Object.keys(chartData.monthlyVolume).length > 0 ? Object.keys(chartData.monthlyVolume).sort() : ['No Data'],
        datasets: [{
            label: 'Ticket Volume',
            data: Object.keys(chartData.monthlyVolume).length > 0 ? Object.values(chartData.monthlyVolume) : [0],
            borderColor: getCssVariable('--primary'),
            backgroundColor: (context) => {
                if (!context.chart?.chartArea) return;
                const ctx = context.chart.ctx;
                const gradient = ctx.createLinearGradient(0, 0, 0, 400);
                const primaryColor = getCssVariable('--primary');
                gradient.addColorStop(0, `${primaryColor}66`);
                gradient.addColorStop(1, `${primaryColor}1a`);
                return gradient;
            },
            fill: true,
            tension: 0.4,
            pointBackgroundColor: getCssVariable('--primary'),
            pointBorderColor: '#fff',
            pointRadius: 4,
            pointHoverRadius: 6,
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: getCssVariable('--primary'),
            pointBorderWidth: 2
        }]
    };

    const pieChartDataConfig = {
        labels: pieChartData.labels,
        datasets: [{
            data: pieChartData.data,
            backgroundColor: pieChartData.backgroundColors,
            borderColor: getCssVariable('--card-bg'),
            borderWidth: 2,
            hoverOffset: 12
        }]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: isMobile ? 'bottom' : 'top', labels: { color: getCssVariable('--text-primary'), font: { size: isMobile ? 9 : 12 } } },
            tooltip: { backgroundColor: getCssVariable('--card-bg'), titleColor: getCssVariable('--text-primary'), bodyColor: getCssVariable('--text-primary') }
        },
        scales: {
            y: { beginAtZero: true, grid: { color: getCssVariable('--chart-grid') }, ticks: { color: getCssVariable('--chart-text'), stepSize: 1 } },
            x: { grid: { color: getCssVariable('--chart-grid') }, ticks: { color: getCssVariable('--chart-text'), maxRotation: isMobile ? 45 : 0 } }
        }
    };

    const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: isMobile ? 'bottom' : 'right', labels: { color: getCssVariable('--text-primary'), font: { size: isMobile ? 9 : 11 } } },
            tooltip: { callbacks: { label: (context) => `${context.label}: ${context.parsed} (${Math.round((context.parsed / context.dataset.data.reduce((a, b) => a + b, 0)) * 100)}%)` } }
        }
    };

    const inProgressPopupColumns = [
        { title: 'Ticket Number', dataIndex: 'ticket_uuid', width: 180, render: (text, record) => <Button type="link" onClick={() => handleViewTicketFromPopup(record.id)}>{text}</Button> },
        { title: 'Summary Title', dataIndex: 'title', width: 280 },
        { title: 'Created Aging', key: 'created_aging', width: 110, render: (_, record) => <Tag color={getAgingColor(record.days_since_created)}>{getAgingDisplay(record.days_since_created, null)}</Tag> },
        { title: 'Assigned Aging', key: 'assigned_aging', width: 110, render: (_, record) => <Tag color={getAgingColor(record.days_since_assigned)}>{getAgingDisplay(record.days_since_assigned, null)}</Tag> },
        { title: 'Status', dataIndex: 'status_name', width: 130 },
        { title: 'Assigned To', dataIndex: 'assigned_to_name', width: 140 },
        { title: 'Action', key: 'action', width: 80, render: (_, record) => <Button type="primary" size="small" icon={<EyeOutlined />} onClick={() => handleViewTicketFromPopup(record.id)}>View</Button> }
    ];

    const pendingSignatureSummaryColumns = [
        { title: 'Ticket Number', dataIndex: 'ticket_uuid', width: 180, render: (text, record) => <Button type="link" onClick={() => { setShowPendingPopup(false); navigate(`/tickets/${record.id}`); }}>{text}</Button> },
        { title: 'Summary Title', dataIndex: 'title', width: 320 },
        { title: 'Pending Signature Type', dataIndex: 'signature_type', width: 210 }
    ];

    const hasInProgress = inProgressTickets.length > 0;
    const hasPendingSignatures = pendingSignatureTickets.length > 0;
    const totalPending = inProgressTickets.length + pendingSignatureTickets.length;

    return (
        <ConfigProvider theme={{ algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
            <div className="dashboard-scrollable">
                <div className="dashboard-header">
                    <div className="header-left">
                        <h1>Partner Dashboard - {mainContractorName}</h1>
                        <span className="role-badge">{roleName}</span>
                        <p className="header-subtitle">
                        Welcome back, {userName}
                        {mainContractorContractIds.length > 0 && !loadingContracts && userContracts.length > 0 && (
                            <>
                                <br />
                                <span className="contract-badge">
                                    Managing {userContracts.length} contract(s): {userContracts.map(c => c.name).join(', ')}
                                </span>
                            </>
                        )}
                        {mainContractorContractIds.length > 0 && loadingContracts && (
                            <>
                                <br />
                                <span className="contract-badge">Loading contracts...</span>
                            </>
                        )}
                    </p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-refresh" onClick={handleRefresh}>
                            <FiRefreshCw /> Refresh Data
                        </button>
                        <button className="btn-primary" onClick={handleCreateTicket}>
                            <FiPlus /> New Ticket
                        </button>
                    </div>
                </div>

                {!dashboardData ? (
                    <div className="dashboard-empty">
                        <p>No dashboard content available.</p>
                    </div>
                ) : (
                    <>
                        {/* Ticket Status Overview Section */}
                        <div className="section-header">
                            <h2>Ticket Status Overview</h2>
                            <button 
                                className={`section-toggle ${showOverviewSection ? 'active' : ''}`} 
                                onClick={toggleOverviewSection}
                            >
                                {showOverviewSection ? <FiEyeOff /> : <FiEye />}
                                {showOverviewSection ? 'Hide Overview' : 'Show Overview'}
                            </button>
                        </div>
                        <div className={`overview-section ${showOverviewSection ? '' : 'hidden'} ${isMobile ? 'mobile' : ''}`}>
                            <div className="cards-grid tier-1">
                                {cardTiers[0].map(card => (
                                    <div 
                                        key={card.type} 
                                        className={`dashboard-card status-card card-${card.size} clickable ${card.isActive ? 'active-view' : ''}`}
                                        style={{ borderLeft: `4px solid ${card.color}` }}
                                        onClick={() => handleCardClick(card.type)}
                                    >
                                        <div className="card-content">
                                            <h3>{card.title}</h3>
                                            <p className="card-value">{card.value}</p>
                                        </div>
                                        <div className="card-icon">
                                            <FiArrowRight className="arrow-icon" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Status Breakdown Section */}
                        <div className="section-header">
                            <h2>
                                Status Breakdown for{' '}
                                {activeView === 'all-tickets' 
                                    ? 'ALL TICKETS' 
                                    : 'MY TICKETS'}
                            </h2>
                            <button 
                                className={`section-toggle ${showBreakdownSection ? 'active' : ''}`} 
                                onClick={toggleBreakdownSection}
                            >
                                {showBreakdownSection ? <FiEyeOff /> : <FiEye />}
                                {showBreakdownSection ? 'Hide Breakdown' : 'Show Breakdown'}
                            </button>
                        </div>
                        <div className={`breakdown-section ${showBreakdownSection ? '' : 'hidden'} ${isMobile ? 'mobile' : ''}`}>
                            <div className="cards-grid tier-2">
                                {cardTiers[1].map(card => (
                                    <div 
                                        key={card.title} 
                                        className={`dashboard-card status-card card-${card.size}`}
                                        style={{ borderLeft: `4px solid ${card.color}` }}
                                    >
                                        <div className="card-content">
                                            <h3>{card.title}</h3>
                                            <p className="card-value">{card.value}</p>
                                        </div>
                                        <div className="card-icon">
                                            <div 
                                                className="status-indicator" 
                                                style={{ backgroundColor: card.color }}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="cards-grid tier-3">
                                {cardTiers[2].map(card => (
                                    <div 
                                        key={card.title} 
                                        className={`dashboard-card status-card card-${card.size}`}
                                        style={{ borderLeft: `4px solid ${card.color}` }}
                                    >
                                        <div className="card-content">
                                            <h3>{card.title}</h3>
                                            <p className="card-value">{card.value}</p>
                                        </div>
                                        <div className="card-icon">
                                            <div 
                                                className="status-indicator" 
                                                style={{ backgroundColor: card.color }}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Analytics Section */}
                        <div className="section-header">
                            <h2>Analytics Overview</h2>
                            <button 
                                className={`section-toggle ${showAnalyticsSection ? 'active' : ''}`} 
                                onClick={toggleAnalyticsSection}
                            >
                                {showAnalyticsSection ? <FiEyeOff /> : <FiEye />}
                                {showAnalyticsSection ? 'Hide Analytics' : 'Show Analytics'}
                            </button>
                        </div>
                        <div className={`charts-section ${showAnalyticsSection ? '' : 'hidden'} ${isMobile ? 'mobile' : ''}`}>
                            <div className="chart-view-switcher">
                                <button 
                                    className={`chart-view-btn ${chartView === 'all-tickets' ? 'active' : ''}`}
                                    onClick={() => handleChartViewChange('all-tickets')}
                                >
                                    All Tickets
                                </button>
                                <button 
                                    className={`chart-view-btn ${chartView === 'my-tickets' ? 'active' : ''}`}
                                    onClick={() => handleChartViewChange('my-tickets')}
                                >
                                    My Tickets
                                </button>
                            </div>
                            <div className="charts-grid">
                                <div className="chart-container">
                                    <div className="chart-header">
                                        <h3>Ticket Volume (Last 6 Months) - {chartView.replace('-', ' ').toUpperCase()}</h3>
                                        <FiTrendingUp />
                                    </div>
                                    <div className="chart-wrapper">
                                        {lineChartData.labels[0] !== 'No Data' ? (
                                            <Line data={lineChartData} options={chartOptions} />
                                        ) : (
                                            <div className="no-chart-data">
                                                <p>No ticket volume data available for {chartView.replace('-', ' ')}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="chart-container">
                                    <div className="chart-header">
                                        <h3>
                                            Ticket Status Distribution - {chartView.replace('-', ' ').toUpperCase()} 
                                            ({pieChartMode === 'current-month' ? currentMonthYear : 'All Time'})
                                        </h3>
                                        <FiPieChart />
                                    </div>
                                    <div className="pie-mode-switcher">
                                        <button 
                                            className={`pie-mode-btn ${pieChartMode === 'current-month' ? 'active' : ''}`}
                                            onClick={() => handlePieChartModeChange('current-month')}
                                        >
                                            Current Month
                                        </button>
                                        <button 
                                            className={`pie-mode-btn ${pieChartMode === 'all-time' ? 'active' : ''}`}
                                            onClick={() => handlePieChartModeChange('all-time')}
                                        >
                                            All Time
                                        </button>
                                    </div>
                                    <div className="chart-wrapper">
                                        <Pie data={pieChartDataConfig} options={pieChartOptions} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Recent Tickets Section */}
                        <div className="recent-tickets-section">
                            <div className="section-header">
                                <h2>
                                    Recent Tickets -{' '}
                                    {chartView === 'all-tickets' 
                                        ? 'ALL TICKETS' 
                                        : 'MY TICKETS'}
                                </h2>
                                <button className="btn-text" onClick={() => navigate('/partner/tickets')}>
                                    View More Tickets
                                </button>
                            </div>
                            {recentTickets.length > 0 ? (
                                <div className={`table-container ${isMobile ? 'mobile-table' : ''}`}>
                                    <table className="tickets-table">
                                        <thead>
                                            <tr>
                                                <th>Ticket Number</th>
                                                <th>Title</th>
                                                <th>Status</th>
                                                <th>Assigned To</th>
                                                <th>Created At</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentTickets.map((ticket) => {
                                                const assigneeName = ticket.assignedTo || 'Unassigned';
                                                const statusName = ticket.statusName || ticket.status || 'Unknown';
                                                
                                                return (
                                                    <tr key={ticket.id} className="ticket-row">
                                                        <td data-label="Ticket Number">
                                                            <span 
                                                                className="ticket-id clickable"
                                                                onClick={() => handleTicketClick(ticket.ticketUuid)}
                                                            >
                                                                {ticket.ticketUuid}
                                                            </span>
                                                        </td>
                                                        <td data-label="Title" className="ticket-title">
                                                            {ticket.title || ticket.subject || 'N/A'}
                                                        </td>
                                                        <td data-label="Status">
                                                            <span 
                                                                className={`status-badge ${statusName.toLowerCase().replace(/\s+/g, '-')}`}
                                                                style={{ backgroundColor: statusColorMap[statusName] || '#6b7280' }}
                                                            >
                                                                {statusName}
                                                            </span>
                                                        </td>
                                                        <td data-label="Assigned To" className="assigned-to">
                                                            <FiUser className="inline-icon" />
                                                            {assigneeName}
                                                        </td>
                                                        <td data-label="Created At" className="created-at">
                                                            <FiCalendar className="inline-icon" />
                                                            {formatDate(ticket.createdAt || ticket.caseReportedDate)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="no-tickets">
                                    <p>No recent tickets found for {chartView.replace('-', ' ').toLowerCase()}</p>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Pending Actions Popup */}
            {(hasInProgress || hasPendingSignatures) && (
                <Modal
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ 
                                width: 40, 
                                height: 40, 
                                borderRadius: '50%', 
                                background: '#faad14', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center' 
                            }}>
                                <ExclamationCircleOutlined style={{ color: 'white', fontSize: 20 }} />
                            </div>
                            <div>
                                <span style={{ fontSize: 18, fontWeight: 600 }}>Pending Actions Required</span>
                                <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                                    Please review and take action on the following tickets
                                </div>
                            </div>
                            <Badge count={totalPending} style={{ backgroundColor: '#faad14', marginLeft: 'auto' }} />
                        </div>
                    }
                    open={showPendingPopup}
                    onCancel={() => setShowPendingPopup(false)}
                    footer={[
                        <Button key="close" onClick={() => setShowPendingPopup(false)}>Close</Button>
                    ]}
                    width={1300}
                    centered
                    destroyOnHidden
                >
                    <Tabs 
                        defaultActiveKey={hasPendingSignatures ? "signatures" : "inprogress"} 
                        size="large"
                        items={[
                            ...(hasPendingSignatures ? [{
                                key: 'signatures',
                                label: <span><SignatureOutlined /> Pending Signatures <Badge count={pendingSignatureTickets.length} /></span>,
                                children: <Table dataSource={pendingSignatureTickets} columns={pendingSignatureSummaryColumns} rowKey="id" pagination={{ pageSize: 5 }} size="small" />
                            }] : []),
                            ...(hasInProgress ? [{
                                key: 'inprogress',
                                label: <span><ClockCircleOutlined /> My Workload <Badge count={inProgressTickets.length} /></span>,
                                children: <Table dataSource={inProgressTickets} columns={inProgressPopupColumns} rowKey="id" pagination={{ pageSize: 5 }} size="small" />
                            }] : [])
                        ]}
                    />
                </Modal>
            )}
        </ConfigProvider>
    );
};

export default PartnerDashboard;